/**
 * AynaraCommandBar — Barra de comandos flotante estilo Slack/⌘K
 * Ejecuta acciones por texto o voz: campañas, posts, WhatsApp, CRM
 * Siempre visible. Sin fricción. Magia en un clic.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Command, Mic, MicOff, Send, Globe, MessageCircle, Video,
  Zap, Users, BarChart3, Mail, X, ChevronRight, Loader2,
  CheckCircle2, Sparkles, ArrowUp
} from 'lucide-react';
import { toast } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Quick actions catalog ─────────────────────────────────────────────────── */
const QUICK_ACTIONS = [
  {
    id: 'campaign_wa',
    icon: MessageCircle,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    label: 'Campaña WhatsApp',
    description: 'Lanza mensajes masivos al CRM',
    keywords: ['whatsapp', 'campaña', 'masivo', 'enviar', 'mensaje', 'wa'],
    tab: 'paises_campaign',
  },
  {
    id: 'post_social',
    icon: Globe,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    label: 'Publicar en Redes',
    description: 'Instagram · TikTok · Facebook vía Publer',
    keywords: ['post', 'publicar', 'instagram', 'tiktok', 'facebook', 'social', 'redes'],
    tab: 'contenido_30',
  },
  {
    id: 'crm_import',
    icon: Users,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    label: 'Importar Contactos',
    description: 'Carga Excel/CSV al CRM',
    keywords: ['importar', 'crm', 'contactos', 'excel', 'base de datos'],
    tab: 'crm_import',
  },
  {
    id: 'ai_message',
    icon: Sparkles,
    color: 'text-primary',
    bg: 'bg-primary/10',
    label: 'Generar Mensaje IA',
    description: 'PNL + Neuromarketing automático',
    keywords: ['generar', 'mensaje', 'ia', 'pnl', 'texto', 'copy', 'redactar'],
    tab: 'paises_campaign',
  },
  {
    id: 'connections',
    icon: Zap,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    label: 'Conexiones',
    description: 'WhatsApp · Publer · Email',
    keywords: ['conectar', 'conexión', 'redes', 'whatsapp', 'publer'],
    tab: 'simple_connections',
  },
  {
    id: 'analytics',
    icon: BarChart3,
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
    label: 'Ver Analíticas',
    description: 'Conversaciones, leads, ventas',
    keywords: ['analytics', 'analítica', 'estadísticas', 'métricas', 'ventas'],
    tab: 'analytics',
  },
  {
    id: 'content_calendar',
    icon: Video,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    label: 'Calendario Contenido 30d',
    description: 'Planifica y programa posts',
    keywords: ['calendario', 'contenido', 'programar', '30 días', 'video'],
    tab: 'contenido_30',
  },
  {
    id: 'intis',
    icon: Zap,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    label: 'Intis · Créditos IA',
    description: 'Saldo, historial y tarifas',
    keywords: ['intis', 'créditos', 'saldo', 'recargar', 'wallet'],
    tab: 'inti_wallet',
  },
];

/* ── Floating Command Bar ──────────────────────────────────────────────────── */
export default function AynaraCommandBar({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [listening, setListening] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);
  const [aiReply, setAiReply] = useState(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  /* Keyboard shortcut: ⌘K / Ctrl+K */
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  /* Fuzzy filter actions */
  const filtered = query.trim()
    ? QUICK_ACTIONS.filter(a =>
        a.label.toLowerCase().includes(query.toLowerCase()) ||
        a.description.toLowerCase().includes(query.toLowerCase()) ||
        a.keywords.some(k => k.includes(query.toLowerCase()))
      )
    : QUICK_ACTIONS;

  const execute = useCallback((action) => {
    setOpen(false);
    setQuery('');
    setAiReply(null);
    if (onNavigate) onNavigate(action.tab);
    toast.success(`Abriendo: ${action.label}`);
  }, [onNavigate]);

  /* Ask Aynara (AI-assisted) */
  const askAynara = async () => {
    if (!query.trim()) return;
    setAiThinking(true);
    setAiReply(null);
    try {
      // Try to match a quick action first
      const matched = QUICK_ACTIONS.find(a =>
        a.keywords.some(k => query.toLowerCase().includes(k))
      );
      if (matched) {
        await new Promise(r => setTimeout(r, 600)); // brief "thinking" pause
        setAiReply({
          text: `Entendido. Te llevo a "${matched.label}".`,
          action: matched,
        });
      } else {
        // Generic response
        setAiReply({
          text: `Reconozco tu comando: "${query}". Usa las acciones rápidas de abajo o escribe algo más específico.`,
          action: null,
        });
      }
    } catch (e) { /* noop */ }
    setAiThinking(false);
  };

  /* Voice recognition */
  const toggleVoice = () => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Tu navegador no soporta reconocimiento de voz. Usa Chrome.');
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = 'es-ES';
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setQuery(transcript);
      setListening(false);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="aynara-command-trigger"
        className="fixed bottom-24 right-6 z-[9990] flex items-center gap-2.5 px-4 py-3 rounded-2xl bg-[#0A1A3F] border border-primary/40 shadow-[0_0_24px_rgba(0,229,255,0.2)] hover:shadow-[0_0_32px_rgba(0,229,255,0.35)] transition-all group"
      >
        <div className="w-7 h-7 rounded-xl bg-primary/20 flex items-center justify-center">
          <Command className="w-4 h-4 text-primary" />
        </div>
        <div className="text-left hidden sm:block">
          <p className="text-[11px] text-primary font-bold leading-none">Aynara</p>
          <p className="text-[9px] text-text-muted leading-none mt-0.5">⌘K para abrir</p>
        </div>
        <span className="hidden sm:flex items-center gap-1 text-[9px] text-text-muted bg-white/5 px-2 py-0.5 rounded-full border border-white/10">
          <Sparkles className="w-2.5 h-2.5 text-primary" /> IA
        </span>
      </button>

      {/* Command palette overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-center pt-20 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setOpen(false); setQuery(''); } }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* Panel */}
          <div
            className="relative w-full max-w-2xl rounded-3xl border border-primary/20 shadow-[0_0_60px_rgba(0,229,255,0.1)] overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #060D1F 0%, #0A1A3F 100%)' }}
            data-testid="aynara-command-panel"
          >
            {/* Input row */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
              <div className="w-8 h-8 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                {aiThinking ? (
                  <Loader2 className="w-4 h-4 text-primary animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 text-primary" />
                )}
              </div>

              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => { setQuery(e.target.value); setAiReply(null); }}
                onKeyDown={e => { if (e.key === 'Enter') askAynara(); }}
                placeholder="Dile algo a Aynara... ej: 'lanza campaña a Perú'"
                className="flex-1 bg-transparent text-text-heading text-sm placeholder-text-muted outline-none"
                data-testid="aynara-command-input"
              />

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={toggleVoice}
                  data-testid="aynara-voice-btn"
                  className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${
                    listening
                      ? 'bg-red-500/20 text-red-400 animate-pulse'
                      : 'bg-white/5 text-text-muted hover:text-primary hover:bg-primary/10'
                  }`}
                  title="Comando por voz"
                >
                  {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>

                {query && (
                  <button
                    onClick={askAynara}
                    className="w-8 h-8 rounded-xl bg-primary/20 flex items-center justify-center text-primary hover:bg-primary/30 transition-all"
                    data-testid="aynara-send-btn"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                )}

                <button
                  onClick={() => { setOpen(false); setQuery(''); setAiReply(null); }}
                  className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center text-text-muted hover:text-text-heading transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* AI reply */}
            {aiReply && (
              <div className="px-5 py-3 border-b border-white/5 bg-primary/5">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-text-body">{aiReply.text}</p>
                    {aiReply.action && (
                      <button
                        onClick={() => execute(aiReply.action)}
                        className="mt-2 flex items-center gap-2 text-xs text-primary hover:underline"
                        data-testid={`aynara-reply-action-${aiReply.action.id}`}
                      >
                        <ChevronRight className="w-3 h-3" />
                        Ir a {aiReply.action.label}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Quick actions grid */}
            <div className="p-4 max-h-80 overflow-y-auto">
              {listening && (
                <div className="flex items-center gap-3 justify-center py-6">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map(i => (
                      <div
                        key={i}
                        className="w-1 bg-primary rounded-full animate-bounce"
                        style={{ height: `${12 + Math.random() * 16}px`, animationDelay: `${i * 0.1}s` }}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-primary animate-pulse">Escuchando...</p>
                </div>
              )}

              {!listening && (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-text-muted mb-3 px-1">
                    {query ? `${filtered.length} resultados` : 'Acciones rápidas'}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {filtered.map(action => (
                      <button
                        key={action.id}
                        onClick={() => execute(action)}
                        data-testid={`aynara-action-${action.id}`}
                        className="flex flex-col items-start gap-2 p-3 rounded-2xl border border-white/5 hover:border-primary/30 hover:bg-white/3 transition-all text-left group"
                      >
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${action.bg}`}>
                          <action.icon className={`w-4 h-4 ${action.color}`} />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-text-heading leading-tight">{action.label}</p>
                          <p className="text-[10px] text-text-muted mt-0.5 leading-tight">{action.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-5 py-2.5 border-t border-white/5 flex items-center gap-4 text-[10px] text-text-muted">
              <span className="flex items-center gap-1"><kbd className="bg-white/10 px-1.5 py-0.5 rounded text-[9px]">↵</kbd> ejecutar</span>
              <span className="flex items-center gap-1"><kbd className="bg-white/10 px-1.5 py-0.5 rounded text-[9px]">Esc</kbd> cerrar</span>
              <span className="flex items-center gap-1"><Mic className="w-2.5 h-2.5" /> voz activado</span>
              <span className="ml-auto flex items-center gap-1 text-primary"><Sparkles className="w-2.5 h-2.5" /> Aynara IA</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
